/**
 * Kukutis product catalog.
 *
 * <kk-catalog-list>  vertical category list (snippets/kk-catalog-list.liquid).
 *   flyout context: one sub-category panel open at a time, opened by the chevron button (click / Enter /
 *   Space / ArrowRight) or by hovering a row with a fine pointer; closed by Escape, a click outside or
 *   leaving the list.
 *   drawer context: sub-categories expand in place, several may be open.
 *
 * <kk-mega-menu>     horizontal category bar with mega menu panels (snippets/kk-mega-menu.liquid).
 *   The chevron button opens a panel (click / Enter / Space / ArrowDown); with a fine pointer, hovering an
 *   item opens it and leaving closes it after a short delay, so moving into the panel keeps it open.
 *   One panel at a time; Escape (focus back to the button) and a click outside close it.
 *
 * <kk-catalog-bar>   "Product catalog" button + quick links (sections/kk-catalog-bar.liquid).
 *   Layout "bar": the menu button only shows when the category bar does not fit (data-compact, measured
 *   on resize) or on phones, and always opens the drawer.
 *   below 990px: opens the navigation drawer (<dialog>, focus kept inside, page scroll locked, focus
 *   returns to the button on close);
 *   from 990px: on the home page toggles the catalog column next to the hero, elsewhere a dropdown.
 */

const DESKTOP = window.matchMedia('(min-width: 990px)');
const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

class KkCatalogList extends HTMLElement {
  #hoverTimer = 0;

  connectedCallback() {
    this.isFlyout = this.dataset.context === 'flyout';
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    if (this.isFlyout) {
      this.addEventListener('pointerover', this.#onPointerOver);
      this.addEventListener('pointerleave', this.#onPointerLeave);
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      document.addEventListener('click', this.onDocumentClick);
    }
  }

  disconnectedCallback() {
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    clearTimeout(this.#hoverTimer);
  }

  get openItems() {
    return [...this.querySelectorAll('.kk-cat__item.is-open')];
  }

  /** @param {HTMLElement} item */
  open(item) {
    const toggle = item.querySelector('[data-kk-cat-toggle]');
    const panel = item.querySelector('.kk-cat__sub');
    if (!toggle || !panel) return;
    if (this.isFlyout) this.openItems.filter((other) => other !== item).forEach((other) => this.close(other));
    item.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  }

  /** @param {HTMLElement} item */
  close(item) {
    const toggle = item.querySelector('[data-kk-cat-toggle]');
    const panel = item.querySelector('.kk-cat__sub');
    item.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  }

  closeAll() {
    this.openItems.forEach((item) => this.close(item));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-kk-cat-toggle]') : null;
    if (!toggle) return;
    const item = toggle.closest('.kk-cat__item');
    if (!(item instanceof HTMLElement)) return;
    clearTimeout(this.#hoverTimer);
    if (item.classList.contains('is-open')) this.close(item);
    else this.open(item);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;
    const item = target.closest('.kk-cat__item');

    if (event.key === 'Escape') {
      const openItem = target.closest('.kk-cat__item.is-open') || this.openItems[0];
      if (openItem instanceof HTMLElement) {
        event.stopPropagation();
        this.close(openItem);
        openItem.querySelector('[data-kk-cat-toggle]')?.focus();
      }
      return;
    }

    if (!this.isFlyout || !(item instanceof HTMLElement)) return;

    if (event.key === 'ArrowRight' && target.matches('[data-kk-cat-toggle]')) {
      event.preventDefault();
      this.open(item);
      item.querySelector('.kk-cat__sub a')?.focus();
    } else if (event.key === 'ArrowLeft' && target.closest('.kk-cat__sub')) {
      event.preventDefault();
      this.close(item);
      item.querySelector('[data-kk-cat-toggle]')?.focus();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (!FINE_POINTER.matches || event.pointerType !== 'mouse') return;
    const item = event.target instanceof Element ? event.target.closest('.kk-cat__item') : null;
    if (!(item instanceof HTMLElement) || item.classList.contains('is-open')) return;
    if (event.target instanceof Element && event.target.closest('.kk-cat__sub')) return;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      if (item.classList.contains('kk-cat__item--parent')) this.open(item);
      else this.closeAll();
    }, 140);
  };

  #onPointerLeave = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeAll();
    }, 300);
  };
}

class KkMegaMenu extends HTMLElement {
  #openTimer = 0;
  #closeTimer = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.closeAll();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
  }

  get openItem() {
    return this.querySelector('.kk-mega__item.is-open');
  }

  /** @param {Element} item */
  open(item) {
    const current = this.openItem;
    if (current && current !== item) this.close(current);
    const toggle = item.querySelector('[data-kk-mega-toggle]');
    const panel = item.querySelector('.kk-mega__panel');
    if (!toggle || !panel) return;
    item.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  }

  /** @param {Element} item */
  close(item) {
    item.classList.remove('is-open');
    item.querySelector('[data-kk-mega-toggle]')?.setAttribute('aria-expanded', 'false');
    const panel = item.querySelector('.kk-mega__panel');
    if (panel instanceof HTMLElement) panel.hidden = true;
  }

  closeAll() {
    clearTimeout(this.#openTimer);
    const item = this.openItem;
    if (item) this.close(item);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-kk-mega-toggle]') : null;
    if (!toggle) return;
    const item = toggle.closest('.kk-mega__item');
    if (!item) return;
    clearTimeout(this.#closeTimer);
    if (item.classList.contains('is-open')) this.close(item);
    else this.open(item);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;
    if (event.key === 'Escape') {
      const item = this.openItem;
      if (!item) return;
      event.stopPropagation();
      this.close(item);
      const toggle = item.querySelector('[data-kk-mega-toggle]');
      if (toggle instanceof HTMLElement) toggle.focus();
    } else if (event.key === 'ArrowDown' && target.matches('[data-kk-mega-toggle]')) {
      event.preventDefault();
      const item = target.closest('.kk-mega__item');
      if (!item) return;
      this.open(item);
      const first = item.querySelector('.kk-mega__panel a');
      if (first instanceof HTMLElement) first.focus();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (!FINE_POINTER.matches || event.pointerType !== 'mouse') return;
    clearTimeout(this.#closeTimer);
    const item = event.target instanceof Element ? event.target.closest('.kk-mega__item') : null;
    if (!item || item.classList.contains('is-open')) return;
    if (event.target instanceof Element && event.target.closest('.kk-mega__panel')) return;
    clearTimeout(this.#openTimer);
    this.#openTimer = window.setTimeout(() => {
      if (item.classList.contains('kk-mega__item--parent')) this.open(item);
      else this.closeAll();
    }, 120);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      if (!this.contains(document.activeElement) || !this.openItem?.contains(document.activeElement)) this.closeAll();
    }, 250);
  };
}

class KkCatalogBar extends HTMLElement {
  connectedCallback() {
    this.isBar = this.dataset.layout === 'bar';
    if (this.isBar) {
      this.onResize = () => this.#updateCompact();
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
      this.#updateCompact();
    }
    this.button = this.querySelector('[data-kk-catbar-button]');
    this.dropdown = this.querySelector('[data-kk-catbar-dropdown]');
    this.drawer = this.querySelector('dialog[data-kk-catbar-drawer]');
    this.button?.addEventListener('click', this.#onButton);
    this.dropdown?.addEventListener('keydown', this.#onDropdownKeydown);
    this.drawer?.addEventListener('click', this.#onDrawerClick);
    this.drawer?.addEventListener('close', this.#onDrawerClose);
    this.onDocumentClick = (event) => {
      if (this.dropdown && !this.dropdown.hidden && !this.contains(event.target)) this.closeDropdown();
    };
    document.addEventListener('click', this.onDocumentClick);
    this.onDocumentKeydown = (event) => {
      if (event.key === 'Escape' && this.dropdown && !this.dropdown.hidden) {
        this.closeDropdown();
        this.button?.focus();
      }
    };
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.onMediaChange = () => {
      this.closeDropdown();
      if (!this.isBar && DESKTOP.matches && this.drawer?.open) this.drawer.close();
      this.#syncHomeState();
    };
    DESKTOP.addEventListener('change', this.onMediaChange);
    this.#syncHomeState();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    DESKTOP.removeEventListener('change', this.onMediaChange);
    this.resizeObserver?.disconnect();
  }

  /** Layout "bar": switch to the menu button when the category bar does not fit or on phones. */
  #updateCompact() {
    const list = this.querySelector('[data-kk-mega-list]');
    const row = this.querySelector('.kk-catbar__row--bar');
    if (!(list instanceof HTMLElement) || !(row instanceof HTMLElement)) return;
    const tooNarrow = window.innerWidth < 750 || list.scrollWidth > row.clientWidth + 1;
    if (tooNarrow !== this.hasAttribute('data-compact')) {
      this.toggleAttribute('data-compact', tooNarrow);
      if (tooNarrow) this.querySelector('kk-mega-menu')?.closeAll();
      else if (this.drawer?.open) this.drawer.close();
    }
  }

  /** The home page renders the catalog column inside the hero section. */
  get homeCatalog() {
    return document.querySelector('[data-kk-home-catalog]');
  }

  #syncHomeState() {
    if (!this.button || this.isBar) return;
    const home = this.homeCatalog;
    if (DESKTOP.matches && home) {
      this.button.setAttribute('aria-controls', home.id);
      this.button.setAttribute('aria-expanded', String(!home.closest('[data-kk-catalog-collapsed]')));
    } else if (DESKTOP.matches && this.dropdown) {
      this.button.setAttribute('aria-controls', this.dropdown.id);
      this.button.setAttribute('aria-expanded', String(!this.dropdown.hidden));
    } else if (this.drawer) {
      this.button.setAttribute('aria-controls', this.drawer.id);
      this.button.setAttribute('aria-expanded', String(this.drawer.open));
    }
  }

  #onButton = (/** @type {MouseEvent} */ event) => {
    event.stopPropagation();
    const home = this.homeCatalog;
    if (this.isBar || !DESKTOP.matches) {
      this.openDrawer();
    } else if (home) {
      const wrapper = home.closest('[data-kk-catalog-wrapper]');
      if (wrapper) wrapper.toggleAttribute('data-kk-catalog-collapsed');
      this.#syncHomeState();
    } else if (this.dropdown) {
      if (this.dropdown.hidden) this.openDropdown();
      else this.closeDropdown();
    }
  };

  openDropdown() {
    if (!this.dropdown) return;
    this.dropdown.hidden = false;
    this.button?.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    if (!this.dropdown || this.dropdown.hidden) return;
    this.dropdown.querySelector('kk-catalog-list')?.closeAll();
    this.dropdown.hidden = true;
    this.button?.setAttribute('aria-expanded', 'false');
  }

  /** @param {KeyboardEvent} event */
  #onDropdownKeydown = (event) => {
    if (event.key !== 'Escape') return;
    // A sub-category panel handles Escape first (it stops propagation); otherwise close the dropdown.
    this.closeDropdown();
    this.button?.focus();
  };

  openDrawer() {
    if (!this.drawer || this.drawer.open) return;
    // Remember who opened the drawer (the bar button or the header menu button) to return focus.
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : this.button;
    this.drawer.showModal();
    document.documentElement.classList.add('kk-scroll-lock');
    this.#setExpanded(true);
    this.drawer.querySelector('[data-kk-catbar-close]')?.focus();
  }

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    this.button?.setAttribute('aria-expanded', String(expanded));
    document.querySelectorAll('[data-kk-menu-toggle]').forEach((el) => el.setAttribute('aria-expanded', String(expanded)));
  }

  #onDrawerClick = (/** @type {MouseEvent} */ event) => {
    if (event.target === this.drawer) this.drawer.close();
    if (event.target instanceof Element && event.target.closest('[data-kk-catbar-close]')) this.drawer?.close();
  };

  #onDrawerClose = () => {
    document.documentElement.classList.remove('kk-scroll-lock');
    this.#setExpanded(false);
    const target = this.opener?.isConnected && this.opener.offsetParent !== null ? this.opener : this.button;
    target?.focus();
  };
}

/** Header menu button on phones (snippets/kk-header-menu-button.liquid): opens the catalog bar drawer. */
class KkMenuToggle extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => {
      const bar = document.querySelector('kk-catalog-bar');
      if (bar && 'openDrawer' in bar) /** @type {any} */ (bar).openDrawer();
    });
  }
}

if (!customElements.get('kk-catalog-list')) customElements.define('kk-catalog-list', KkCatalogList);
if (!customElements.get('kk-menu-toggle')) customElements.define('kk-menu-toggle', KkMenuToggle);
if (!customElements.get('kk-mega-menu')) customElements.define('kk-mega-menu', KkMegaMenu);
if (!customElements.get('kk-catalog-bar')) customElements.define('kk-catalog-bar', KkCatalogBar);
